import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Discount as DiscountIcon,
  Clear as ClearIcon,
  Block as VoidIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { transactionsAPI, settingsAPI, apiUtils } from '../services/api';
import ThermalReceiptPreview from '../components/ThermalReceiptPreview';
import { printerService } from '../services/printer';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [receiptPreviewOpen, setReceiptPreviewOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidingTransaction, setVoidingTransaction] = useState(false);
  const [page, setPage] = useState(0); // DataGrid uses 0-based indexing
  const [pageSize, setPageSize] = useState(15);
  const [totalRows, setTotalRows] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Thermal printer settings
  const [thermalWidth, setThermalWidth] = useState(58);
  
  // Printer settings state
  const [printerSettings, setPrinterSettings] = useState(null);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadTransactions();
    loadThermalSettings();
  }, []);

  // Helper function to show notifications
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  const loadThermalSettings = async () => {
    try {
      const response = await settingsAPI.fetch();
      const settings = apiUtils.formatResponse(response);
      
      if (settings) {
        setThermalWidth(settings.thermal_printer_width || 58);
        
        // Load printer settings from localStorage (device-specific, not from API)
        loadPrinterSettingsFromLocalStorage();
      }
    } catch (error) {
      console.error("Error loading thermal settings:", error);
      // Continue with default if loading fails
    }
  };

  // Function to load printer settings from localStorage
  const loadPrinterSettingsFromLocalStorage = () => {
    try {
      const printerType = localStorage.getItem('printer-type') || 'browser';
      const qzTrayEnabled = localStorage.getItem('qz-tray-enabled') === 'true';
      const qzTrayPrinterName = localStorage.getItem('qz-tray-printer-name') || null;
      const rawbtEnabled = localStorage.getItem('rawbt-enabled') === 'true';
      const rawbtDeviceName = localStorage.getItem('rawbt-device-name') || null;
      const rawbtDeviceAddress = localStorage.getItem('rawbt-device-address') || null;
      
      console.log("Loading printer settings from localStorage:", {
        printerType, qzTrayEnabled, qzTrayPrinterName, rawbtEnabled, rawbtDeviceName, rawbtDeviceAddress
      });
      
      setPrinterSettings({
        printer_type: printerType,
        qz_tray_enabled: qzTrayEnabled,
        qz_tray_printer_name: qzTrayPrinterName,
        rawbt_enabled: rawbtEnabled,
        rawbt_device_name: rawbtDeviceName,
        rawbt_device_address: rawbtDeviceAddress
      });
    } catch (error) {
      console.error("Error loading printer settings from localStorage:", error);
      // Use defaults
      setPrinterSettings({
        printer_type: 'browser',
        qz_tray_enabled: false,
        qz_tray_printer_name: null,
        rawbt_enabled: false,
        rawbt_device_name: null,
        rawbt_device_address: null
      });
    }
  };

  const loadTransactions = async (pageNum = 1, search = '') => {
    console.log('Loading transactions for page:', pageNum, 'search:', search);
    setLoading(true);
    try {
      let response;
      if (search) {
        response = await transactionsAPI.search(search, pageNum);
      } else {
        response = await transactionsAPI.list(pageNum);
      }
      
      const transactionsData = apiUtils.formatResponse(response) || [];
      console.log('Loaded transactions count:', transactionsData.length);
      
      // For server-side pagination, always replace the data
      setTransactions(transactionsData);
      
      // Calculate total rows for pagination
      // Since we don't have total count from API, estimate based on page size
      let calculatedTotalRows;
      if (transactionsData.length < pageSize) {
        // This is the last page
        calculatedTotalRows = (pageNum - 1) * pageSize + transactionsData.length;
      } else {
        // There might be more pages, set a high estimate
        calculatedTotalRows = pageNum * pageSize + 1;
      }
      
      setTotalRows(calculatedTotalRows);
      console.log('Total rows set to:', calculatedTotalRows);
      setError('');
    } catch (error) {
      console.error('Error loading transactions:', error);
      setError(apiUtils.handleError(error));
      showNotification('Failed to load transactions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setPage(0); // Reset to first page
    if (searchTerm.trim()) {
      await loadTransactions(1, searchTerm.trim());
    } else {
      await loadTransactions(1);
    }
  };

  const clearSearch = async () => {
    setSearchTerm('');
    setPage(0); // Reset to first page
    await loadTransactions(1);
  };

  const handleViewDetails = async (transaction) => {
    setSelectedTransaction(transaction);
    try {
      const response = await transactionsAPI.fetchDetails(transaction.id);
      const details = apiUtils.formatResponse(response);
      setTransactionDetails(details);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      showNotification('Failed to load transaction details', 'error');
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    console.log('Page change requested:', newPage);
    if (newPage !== page) {
      setPage(newPage);
      loadTransactions(newPage + 1, searchTerm); // Convert to 1-based indexing for API
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    console.log('Page size change requested:', newPageSize);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setPage(0);
      loadTransactions(1, searchTerm);
    }
  };

  const handleReceiptPreview = async (transaction) => {
    setSelectedTransaction(transaction);
    try {
      const response = await transactionsAPI.fetchDetails(transaction.id);
      const details = apiUtils.formatResponse(response);
      setTransactionDetails(details);
      
      // Check if advanced printing is configured
      if (printerSettings && printerSettings.printer_type !== 'browser') {
        try {
          // Let printer service load its own fresh settings
          // printerService.settings = printerSettings; // REMOVED - let service load fresh settings
          
          // Create complete receipt data with transaction details
          const completeReceiptData = {
            ...transaction,
            transactionDetails: details
          };
          
          // Try to print using advanced printer service
          await printerService.printReceipt(completeReceiptData);
          showNotification('Receipt printed successfully', 'success');
        } catch (error) {
          console.error('Advanced printing failed:', error);
          
          if (error.message === 'BROWSER_PRINT_REQUESTED') {
            // Printer service is requesting browser print - show preview
            console.log('Showing receipt preview as requested by printer service');
            setReceiptPreviewOpen(true);
          } else {
            showNotification(`Printing failed: ${error.message}. Opening print preview...`, 'warning');
            
            // Fall back to browser print preview
            setReceiptPreviewOpen(true);
          }
        }
      } else {
        // Browser printing is selected - show preview directly
        console.log('Browser printing selected - showing preview');
        setReceiptPreviewOpen(true);
      }
    } catch (error) {
      console.error('Error fetching transaction details for receipt:', error);
      showNotification('Failed to load transaction details', 'error');
    }
  };

  const handleVoidTransaction = () => {
    setVoidDialogOpen(true);
  };

  const confirmVoidTransaction = async () => {
    if (!selectedTransaction) return;

    setVoidingTransaction(true);
    try {
      const statusData = {
        to_status: 'CANCELLED',
        comment: 'Transaction voided by user'
      };

      await transactionsAPI.updateStatus(selectedTransaction.id, statusData);
      
      // Update the transaction in the local state
      setTransactions(prev => 
        prev.map(t => 
          t.id === selectedTransaction.id 
            ? { ...t, status: 'CANCELLED' }
            : t
        )
      );

      // Update selected transaction
      setSelectedTransaction(prev => ({ ...prev, status: 'CANCELLED' }));
      
      setVoidDialogOpen(false);
      showNotification('Transaction has been voided successfully', 'success');
    } catch (error) {
      console.error('Error voiding transaction:', error);
      showNotification('Failed to void transaction', 'error');
    } finally {
      setVoidingTransaction(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'voided': return 'error';
      default: return 'default';
    }
  };

  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row)}
            color="primary"
            title="View Details"
          >
            <ViewIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleReceiptPreview(params.row)}
            color="secondary"
            title="Receipt Preview"
          >
            <PrintIcon />
          </IconButton>
        </Box>
      ),
    },
    { 
      field: 'txn_no', 
      headerName: 'Transaction #', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <Typography variant="body2" fontFamily="monospace">
            {params.value || 'N/A'}
            </Typography>
        </Box>
      )
    },
    { 
      field: 'customer', 
      headerName: 'Customer', 
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.row.customer?.display_name || 'Walk-in'}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'grand_total', 
      headerName: 'Total Amount', 
      width: 120,
      type: 'number',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <Typography variant="body2" fontWeight="medium">
              {formatCurrency(params.value)}
            </Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Unknown'}
          color={getStatusColor(params.value)}
          size="small"
          variant={params.value?.toLowerCase() === 'cancelled' ? 'filled' : 'outlined'}
          icon={params.value?.toLowerCase() === 'cancelled' ? <VoidIcon /> : undefined}
          sx={{
            fontWeight: params.value?.toLowerCase() === 'cancelled' ? 'bold' : 'normal'
          }}
        />
      ),
    },
    { 
      field: 'date_created', 
      headerName: 'Date Created', 
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <Typography variant="body2">
              {formatDateTime(params.value)}
            </Typography>
        </Box>
      )
    },
    { 
      field: 'note', 
      headerName: 'Note', 
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <Typography variant="body2" sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {params.value || 'No note'}
            </Typography>
        </Box>
      )
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" component="h1">
          Transactions
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexShrink: 0 }}>
        <TextField
          placeholder="Search by transaction number or note..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading}>
          Search
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {/* Data Grid */}
      <Paper 
        sx={{ 
          height: 'calc(100vh - 240px)', 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <DataGrid
          rows={transactions}
          columns={columns}
          paginationModel={{ page: page, pageSize: pageSize }}
          pageSizeOptions={[15, 25, 50]}
          rowCount={totalRows}
          loading={loading}
          disableSelectionOnClick
          autoHeight={false}
          pagination
          paginationMode="server"
          onPaginationModelChange={(model) => {
            console.log('Pagination model change:', model);
            if (model.page !== page) {
              handlePageChange(model.page);
            }
            if (model.pageSize !== pageSize) {
              handlePageSizeChange(model.pageSize);
            }
          }}
          getRowClassName={(params) => 
            params.row.status?.toLowerCase() === 'cancelled' ? 'voided-row' : ''
          }
          sx={{
            height: '100%',
            width: '100%',
            border: 'none',
            '& .MuiDataGrid-main': {
              overflow: 'auto',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto !important',
            },
            '& .voided-row': {
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.2)',
              },
              '& .MuiDataGrid-cell': {
                opacity: 0.8,
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              minHeight: '52px',
              backgroundColor: 'background.paper',
              justifyContent: 'flex-start',
              '& .MuiTablePagination-root': {
                marginLeft: 0,
              },
              '& .MuiTablePagination-toolbar': {
                justifyContent: 'flex-start',
              },
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Paper>

      {/* Transaction Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon />
          Transaction Details - {selectedTransaction?.txn_no}
          {selectedTransaction?.status?.toLowerCase() === 'cancelled' && (
            <Chip
              label="VOIDED"
              color="error"
              size="small"
              icon={<VoidIcon />}
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {transactionDetails && selectedTransaction && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Transaction Summary */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Transaction Summary</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Transaction #:</Typography>
                      <Typography variant="body1" fontFamily="monospace">{selectedTransaction.txn_no}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Date:</Typography>
                      <Typography variant="body1">{formatDateTime(selectedTransaction.date_created)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Customer:</Typography>
                      <Typography variant="body1">{selectedTransaction.customer?.display_name || 'Walk-in'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Chip
                        label={selectedTransaction.status || 'Unknown'}
                        color={getStatusColor(selectedTransaction.status)}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                      <Typography variant="body1">{formatCurrency(selectedTransaction.subtotal)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Total Discounts:</Typography>
                      <Typography variant="body1" color="error">{formatCurrency(selectedTransaction.total_discounts)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Grand Total:</Typography>
                      <Typography variant="h6" color="primary">{formatCurrency(selectedTransaction.grand_total)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Payment Total:</Typography>
                      <Typography variant="body1">{formatCurrency(selectedTransaction.payment_total)}</Typography>
                    </Box>
                  </Box>
                  {selectedTransaction.note && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Note:</Typography>
                      <Typography variant="body1">{selectedTransaction.note}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Transaction Items */}
              {transactionDetails.details && transactionDetails.details.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Items</Typography>
                    <List dense>
                      {transactionDetails.details.map((item, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {item.product?.name || `Product ID: ${item.product_id}`}
                                    </Typography>
                                    {item.note === 'COMPLIMENTARY' && (
                                      <Chip 
                                        label="COMPLIMENTARY" 
                                        size="small" 
                                        color="success"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatCurrency(item.line_price)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Qty: {item.qty} × {formatCurrency(item.unit_price)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    SKU: {item.product?.sku || 'N/A'}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < transactionDetails.details.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Discounts */}
              {transactionDetails.discounts && transactionDetails.discounts.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DiscountIcon /> Discounts Applied
                    </Typography>
                    <List dense>
                      {transactionDetails.discounts.map((discount, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={discount.discount_type}
                            secondary={
                              <Box>
                                <Typography variant="caption">
                                  {discount.discount_percent > 0 
                                    ? `${discount.discount_percent}%` 
                                    : formatCurrency(discount.discount_amount)
                                  }
                                </Typography>
                                {(discount.reference1 || discount.reference2) && (
                                  <Typography variant="caption" sx={{ ml: 2 }}>
                                    Ref: {discount.reference1} {discount.reference2}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Payments */}
              {transactionDetails.payments && transactionDetails.payments.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaymentIcon /> Payments
                    </Typography>
                    <List dense>
                      {transactionDetails.payments.map((payment, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">{payment.payment_type}</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatCurrency(payment.payment_amount)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              (payment.reference1 || payment.reference2) && (
                                <Typography variant="caption">
                                  Ref: {payment.reference1} {payment.reference2}
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTransaction?.status?.toLowerCase() !== 'cancelled' && (
            <Button
              onClick={handleVoidTransaction}
              color="error"
              variant="outlined"
              startIcon={<VoidIcon />}
              sx={{ mr: 'auto' }}
            >
              Void Transaction
            </Button>
          )}
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Void Confirmation Dialog */}
      <Dialog
        open={voidDialogOpen}
        onClose={() => setVoidDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Confirm Void Transaction
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to void this transaction?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Transaction #:</strong> {selectedTransaction?.txn_no}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Amount:</strong> {formatCurrency(selectedTransaction?.grand_total)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Customer:</strong> {selectedTransaction?.customer?.display_name || 'Walk-in'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {formatDateTime(selectedTransaction?.date_created)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Voiding this transaction will mark it as invalid and it cannot be reversed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setVoidDialogOpen(false)}
            disabled={voidingTransaction}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmVoidTransaction}
            color="error"
            variant="contained"
            disabled={voidingTransaction}
            startIcon={<VoidIcon />}
          >
            {voidingTransaction ? 'Voiding...' : 'Void Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Thermal Receipt Preview Dialog */}
      <Dialog
        open={receiptPreviewOpen}
        onClose={() => setReceiptPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon />
          Thermal Receipt Preview - {selectedTransaction?.txn_no}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Preview of how the receipt will look on a {thermalWidth}mm thermal printer:
            </Typography>
            <ThermalReceiptPreview
              transaction={{
                ...selectedTransaction,
                service_charge_inclusive: Boolean(selectedTransaction?.service_charge_inclusive)
              }}
              transactionDetails={transactionDetails}
              thermalWidth={thermalWidth}
              showPrintButton={true}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Transactions;
